import { forEachDiagnostic } from "@codemirror/lint";
import { StateEffect, StateField } from "@codemirror/state";
import { EditorView, ViewPlugin, showTooltip } from "@codemirror/view";

import { errorsToDiagnostics } from "./errors-to-diagnostics";
import { mergeCompatibleOverlappingDiagnostics } from "./merge-overlapping-diagnostics";

function enrichDiagnostic(diagnostic, index, doc, from = diagnostic.from, to = diagnostic.to) {
    const line = doc.lineAt(Math.max(0, Math.min(from, doc.length)));
    return {
        ...diagnostic,
        from,
        to,
        source: "syntax",
        entryIndex: index,
        row: line.number - 1,
        line: line.number,
        column: Math.max(1, from - line.from + 1),
        endColumn: Math.max(1, to - line.from + 1),
    };
}

const setLatexSyntaxMarkerTooltipEffect = StateEffect.define();

const latexSyntaxMarkerTooltipState = StateField.define({
    create() {
        return null;
    },
    update(value, transaction) {
        for (const effect of transaction.effects) {
            if (effect.is(setLatexSyntaxMarkerTooltipEffect)) {
                return effect.value;
            }
        }

        if (transaction.docChanged) {
            return null;
        }

        return value;
    },
    provide: (field) => showTooltip.from(field),
});

function createLatexSyntaxMarkerTooltip(payload, marker) {
    return {
        pos: payload.pos,
        above: false,
        clip: false,
        create() {
            const dom = document.createElement("div");
            dom.className = "cm-tooltip-lint cm-tooltip-latex-error-fix";
            dom.dataset.latexErrorFixTooltip = "true";
            dom.dataset.line = String(payload.line);
            dom.setAttribute("role", "button");
            dom.setAttribute("tabindex", "0");

            for (const diagnostic of payload.diagnostics) {
                const item = document.createElement("div");
                item.className = "cm-diagnostic cm-diagnostic-error";
                item.textContent = diagnostic.message;
                dom.appendChild(item);
            }

            return {
                dom,
                getCoords: () => marker.getBoundingClientRect(),
            };
        },
    };
}

export function latexSyntaxErrorClickExtension(onClick) {
    const collectAllSyntaxDiagnostics = (view) => {
        const diagnostics = [];
        forEachDiagnostic(view.state, (diagnostic, from, to) => {
            if (diagnostic.source !== "syntax" || diagnostic.severity !== "error") {
                return;
            }
            diagnostics.push(enrichDiagnostic(diagnostic, diagnostics.length, view.state.doc, from, to));
        });
        return diagnostics;
    };

    const collectSyntaxDiagnosticsForMarker = (event, view) => {
        const directMarker = event.target?.closest?.(".cm-lint-marker-error");
        const gutterElement =
            directMarker?.closest(".cm-gutterElement") || event.target?.closest?.(".cm-gutterElement");
        if (!gutterElement) {
            return null;
        }
        const marker = directMarker || gutterElement.querySelector(".cm-lint-marker-error");
        if (!marker) {
            return null;
        }

        const markerRect = marker.getBoundingClientRect();
        const block = view.elementAtHeight(markerRect.top + 5 - view.documentTop);
        const lineDiagnostics = [];

        forEachDiagnostic(view.state, (diagnostic, from, to) => {
            if (diagnostic.source !== "syntax" || diagnostic.severity !== "error") {
                return;
            }
            const diagnosticLine = view.state.doc.lineAt(from);
            const blockLine = view.state.doc.lineAt(block.from);
            const isSameVisibleLine = diagnosticLine.number === blockLine.number;
            const isInsideReplacedBlock = from > block.from && from < block.to;
            if (!isSameVisibleLine && !isInsideReplacedBlock) {
                return;
            }
            lineDiagnostics.push(enrichDiagnostic(diagnostic, lineDiagnostics.length, view.state.doc, from, to));
        });

        if (!lineDiagnostics.length) {
            return null;
        }

        const firstDiagnostic = lineDiagnostics[0];
        return {
            line: firstDiagnostic.line,
            pos: block.from,
            marker,
            diagnostics: lineDiagnostics,
        };
    };

    const showMarkerTooltip = (event, view) => {
        const marker = event.target?.closest?.(".cm-lint-marker-error");
        if (!marker) {
            return false;
        }
        const payload = collectSyntaxDiagnosticsForMarker(event, view);
        if (!payload) {
            return false;
        }
        view.dispatch({
            effects: setLatexSyntaxMarkerTooltipEffect.of(createLatexSyntaxMarkerTooltip(payload, marker)),
        });
        return false;
    };

    const hideMarkerTooltip = (event, view) => {
        if (!event.target?.closest?.(".cm-lint-marker-error")) {
            return false;
        }
        const relatedTarget = event.relatedTarget;
        if (relatedTarget?.closest?.("[data-latex-error-fix-tooltip='true']")) {
            return false;
        }
        view.dispatch({
            effects: setLatexSyntaxMarkerTooltipEffect.of(null),
        });
        return false;
    };

    const openSyntaxErrorFix = (event, view) => {
        const payload = collectSyntaxDiagnosticsForMarker(event, view);
        if (!payload) {
            return false;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation?.();
        view.dispatch({
            effects: setLatexSyntaxMarkerTooltipEffect.of(null),
        });
        onClick?.({
            ...payload,
            diagnostics: collectAllSyntaxDiagnostics(view),
            markerDiagnostics: payload.diagnostics,
        });
        return true;
    };

    const openSyntaxErrorFixFromTooltip = (event, view) => {
        const tooltip = event.target?.closest?.("[data-latex-error-fix-tooltip='true']");
        if (!tooltip) {
            return false;
        }
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation?.();

        const tooltipLine = Number.parseInt(tooltip.dataset.line || "", 10);
        const diagnostics = [];
        forEachDiagnostic(view.state, (diagnostic, from, to) => {
            if (diagnostic.source !== "syntax" || diagnostic.severity !== "error") {
                return;
            }
            const enriched = enrichDiagnostic(diagnostic, diagnostics.length, view.state.doc, from, to);
            if (Number.isFinite(tooltipLine) && enriched.line !== tooltipLine) {
                return;
            }
            diagnostics.push(enriched);
        });
        if (!diagnostics.length) {
            return false;
        }
        const first = diagnostics[0];
        view.dispatch({
            effects: setLatexSyntaxMarkerTooltipEffect.of(null),
        });
        onClick?.({
            line: first.line,
            pos: first.from,
            diagnostics: collectAllSyntaxDiagnostics(view),
            markerDiagnostics: diagnostics.filter((diagnostic) => diagnostic.line === first.line),
        });
        return true;
    };

    return [
        latexSyntaxMarkerTooltipState,
        EditorView.baseTheme({
            ".cm-tooltip-latex-error-fix": {
                cursor: "pointer",
            },
        }),
        ViewPlugin.define((view) => {
            let suppressNextClick = false;
            const handleMouseOver = (event) => {
                showMarkerTooltip(event, view);
            };
            const handleMouseOut = (event) => {
                hideMarkerTooltip(event, view);
            };
            const handleClick = (event) => {
                if (suppressNextClick) {
                    suppressNextClick = false;
                    event.preventDefault();
                    event.stopPropagation();
                    event.stopImmediatePropagation?.();
                    return;
                }
                openSyntaxErrorFix(event, view) || openSyntaxErrorFixFromTooltip(event, view);
            };
            const handlePointerDown = (event) => {
                if (openSyntaxErrorFix(event, view)) {
                    suppressNextClick = true;
                }
            };

            view.dom.addEventListener("mouseover", handleMouseOver, true);
            view.dom.addEventListener("mouseout", handleMouseOut, true);
            view.dom.addEventListener("pointerdown", handlePointerDown, true);
            view.dom.addEventListener("click", handleClick, true);
            document.addEventListener("click", handleClick, true);

            return {
                destroy() {
                    view.dom.removeEventListener("mouseover", handleMouseOver, true);
                    view.dom.removeEventListener("mouseout", handleMouseOut, true);
                    view.dom.removeEventListener("pointerdown", handlePointerDown, true);
                    view.dom.removeEventListener("click", handleClick, true);
                    document.removeEventListener("click", handleClick, true);
                },
            };
        }),
    ];
}

export function latexSyntaxDiagnosticTooltipFilter(diagnostics) {
    return diagnostics.filter((diagnostic) => diagnostic.source !== "syntax" || diagnostic.severity !== "error");
}

const lintWorker = new Worker(
    /* webpackChunkName: "latex-linter-worker" */
    new URL("./latex-linter.worker.js", import.meta.url),
);
class Deferred {
    constructor() {
        this.promise = new Promise((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;
        });
    }
}
let linterPromise = null; // promise which will resolve to results of the current linting
let queuedRequest = null; // deferred promise for incoming linting requests while the current one is running
let currentView = null;
let currentResolver = null;
let currentRejector = null;
const runLinter = () => {
    lintWorker.postMessage({ text: currentView.state.doc.toString() });
    return new Promise((resolve, reject) => {
        currentResolver = resolve;
        currentRejector = reject;
    });
};
lintWorker.addEventListener("message", (event) => {
    if (event.data && event.data.errors && currentView) {
        const errors = event.data.errors;
        const editorState = currentView.state;
        const doc = editorState.doc;
        const cursorPosition = editorState.selection.main.head;
        const diagnostics = errorsToDiagnostics(errors, cursorPosition, doc.length);
        const syntaxDiagnostics = diagnostics.map((diagnostic) => ({
            ...diagnostic,
            source: "syntax",
        }));
        const mergedDiagnostics = mergeCompatibleOverlappingDiagnostics(syntaxDiagnostics);
        const enrichedDiagnostics = mergedDiagnostics.map((diagnostic, index) =>
            enrichDiagnostic(diagnostic, index, doc),
        );
        currentResolver(enrichedDiagnostics);
        // make compile controller aware of lint errors via editor:lint event
        const lintErrors = errors.filter((e) => e.type !== "info");
        const firstLintError = lintErrors[0] || null;
        const hasLintingError = lintErrors.length > 0;
        window.dispatchEvent(
            new CustomEvent("editor:lint", {
                detail: {
                    hasLintingError,
                    diagnostics: enrichedDiagnostics,
                    firstError: firstLintError
                        ? {
                              row: Number.isFinite(firstLintError.start_row)
                                  ? firstLintError.start_row
                                  : firstLintError.row,
                              column: Number.isFinite(firstLintError.start_col)
                                  ? firstLintError.start_col
                                  : firstLintError.column,
                              endRow: Number.isFinite(firstLintError.end_row)
                                  ? firstLintError.end_row
                                  : firstLintError.row,
                              endColumn: Number.isFinite(firstLintError.end_col)
                                  ? firstLintError.end_col
                                  : firstLintError.column,
                              message: firstLintError.text || "",
                          }
                        : null,
                },
            }),
        );
        currentResolver = null;
        currentRejector = null;
    }
});
lintWorker.addEventListener("error", (event) => {
    console.error("latex linter worker failed", event);
    if (currentRejector) {
        currentRejector(event.error || new Error("latex linter worker failed"));
        currentRejector = null;
    }
    if (currentResolver) {
        currentResolver([]);
        currentResolver = null;
    }
    queuedRequest = null;
    linterPromise = null;
});
const executeQueuedAction = (deferred) => {
    runLinter()
        .then((result) => deferred.resolve(result))
        .catch((error) => {
            console.error("latex linter run failed", error);
            deferred.resolve([]);
        });
    return deferred.promise;
};
const processQueue = () => {
    if (queuedRequest) {
        linterPromise = executeQueuedAction(queuedRequest).then(processQueue);
        queuedRequest = null;
    } else {
        linterPromise = null;
    }
};
export const latexLinter = (view) => {
    // always update the view, we use it to filter the results to the current buffer
    currentView = view;
    // if a linting request isn't already running, start it running
    if (!linterPromise) {
        linterPromise = runLinter();
        linterPromise
            .catch((error) => {
                console.error("latex linter run failed", error);
                return [];
            })
            .then(processQueue);
        return linterPromise;
    } else {
        // otherwise create a single deferred promise which we will return to all subsequent requests
        if (!queuedRequest) {
            queuedRequest = new Deferred();
        }
        return queuedRequest.promise;
    }
};
